import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';

/**
 * Filters for history queries
 */
export interface HistoryFilters {
  from_date?: string;
  to_date?: string;
  state_id?: string;
  transition_id?: string;
  user_id?: string;
  limit?: number;
  offset?: number;
}

/**
 * Validation options
 */
export interface ValidationOptions {
  validate_completeness?: boolean;
  validate_reachability?: boolean;
  validate_consistency?: boolean;
  identify_bottlenecks?: boolean;
}

/**
 * Analytics filters
 */
export interface AnalyticsFilters {
  from_date?: string;
  to_date?: string;
  state_id?: string;
  group_by?: 'day' | 'week' | 'month' | 'state' | 'transition';
}

/**
 * Execution history entry
 */
export interface ExecutionHistoryEntry {
  id: string;
  artifact_id: string;
  execution_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  executed_by?: string;
  metadata?: Record<string, unknown>;
}

/**
 * State history entry
 */
export interface StateHistoryEntry {
  id: string;
  artifact_id: string;
  item_id: string;
  from_state_id?: string;
  to_state_id: string;
  transition_id?: string;
  transitioned_at: string;
  transitioned_by?: string;
  reason?: string;
}

/**
 * Transition history entry
 */
export interface TransitionHistoryEntry {
  id: string;
  artifact_id: string;
  item_id: string;
  transition_id: string;
  from_state_id?: string;
  to_state_id: string;
  executed_at: string;
  executed_by?: string;
  status: string;
  metadata?: Record<string, unknown>;
}

/**
 * Workflow progress
 */
export interface WorkflowProgress {
  artifact_id: string;
  total_items: number;
  items_by_state: Record<string, number>;
  completed_items: number;
  in_progress_items: number;
  blocked_items: number;
  progress_percentage: number;
  last_activity?: string;
}

/**
 * State distribution
 */
export interface StateDistribution {
  state_id: string;
  state_name: string;
  item_count: number;
  percentage: number;
}

/**
 * Artifact state summary
 */
export interface ArtifactStateSummary {
  artifact_id: string;
  total_states: number;
  total_transitions: number;
  states: Array<{
    id: string;
    name: string;
    type: string;
    item_count: number;
  }>;
  transitions: Array<{
    id: string;
    name: string;
    from_state: string;
    to_state: string;
    execution_count: number;
  }>;
}

/**
 * Artifact item summary
 */
export interface ArtifactItemSummary {
  artifact_id: string;
  total_items: number;
  items_by_state: Array<{
    state_id: string;
    state_name: string;
    item_count: number;
  }>;
  recent_items: Array<{
    id: string;
    name: string;
    current_state: string;
    created_at: string;
  }>;
}

/**
 * Workflow validation result
 */
export interface WorkflowValidationResult {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  completeness_score?: number;
  reachability_score?: number;
  consistency_score?: number;
}

/**
 * Service for state machine reporting and analytics
 */
export class StateMachineReportingService extends BaseService {
  /**
   * Get execution history for an artifact
   */
  static async getExecutionHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<ExecutionHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.EXECUTION_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.EXECUTION_HISTORY(artifactId);

      const response = await this.get<ExecutionHistoryEntry[] | { history: ExecutionHistoryEntry[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'history' in response) {
        return (response as { history: ExecutionHistoryEntry[] }).history;
      }
      return [];
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching execution history:', error);
      return [];
    }
  }

  /**
   * Get state change history for an artifact
   */
  static async getStateHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<StateHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.STATE_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.STATE_HISTORY(artifactId);

      const response = await this.get<StateHistoryEntry[] | { history: StateHistoryEntry[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'history' in response) {
        return (response as { history: StateHistoryEntry[] }).history;
      }
      return [];
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching state history:', error);
      return [];
    }
  }

  /**
   * Get transition history for an artifact
   */
  static async getTransitionHistory(
    artifactId: string,
    filters?: HistoryFilters
  ): Promise<TransitionHistoryEntry[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.transition_id) params.append('transition_id', filters.transition_id);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.TRANSITION_HISTORY(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.TRANSITION_HISTORY(artifactId);

      const response = await this.get<TransitionHistoryEntry[] | { history: TransitionHistoryEntry[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'history' in response) {
        return (response as { history: TransitionHistoryEntry[] }).history;
      }
      return [];
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching transition history:', error);
      return [];
    }
  }

  /**
   * Get workflow progress for an artifact
   */
  static async getWorkflowProgress(artifactId: string): Promise<WorkflowProgress | null> {
    try {
      return await this.get<WorkflowProgress>(API_ENDPOINTS.STATE_MACHINE.WORKFLOW_PROGRESS(artifactId));
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching workflow progress:', error);
      return null;
    }
  }

  /**
   * Validate workflow for an artifact
   */
  static async validateWorkflow(
    artifactId: string,
    options?: ValidationOptions
  ): Promise<WorkflowValidationResult> {
    try {
      const defaultOptions: ValidationOptions = {
        validate_completeness: true,
        validate_reachability: true,
        validate_consistency: true,
        identify_bottlenecks: false,
        ...options,
      };

      return await this.get<WorkflowValidationResult>(
        `${API_ENDPOINTS.STATE_MACHINE.VALIDATE_WORKFLOW(artifactId)}?${new URLSearchParams({
          validate_completeness: String(defaultOptions.validate_completeness ?? true),
          validate_reachability: String(defaultOptions.validate_reachability ?? true),
          validate_consistency: String(defaultOptions.validate_consistency ?? true),
          identify_bottlenecks: String(defaultOptions.identify_bottlenecks ?? false),
        }).toString()}`
      );
    } catch (error) {
      console.error('[StateMachineReportingService] Error validating workflow:', error);
      return {
        is_valid: false,
        errors: ['Failed to validate workflow'],
        warnings: [],
      };
    }
  }

  /**
   * Get workflow analytics for an artifact
   */
  static async getWorkflowAnalytics(
    artifactId: string,
    filters?: AnalyticsFilters
  ): Promise<any> {
    try {
      const params = new URLSearchParams();
      if (filters?.from_date) params.append('from_date', filters.from_date);
      if (filters?.to_date) params.append('to_date', filters.to_date);
      if (filters?.state_id) params.append('state_id', filters.state_id);
      if (filters?.group_by) params.append('group_by', filters.group_by);

      const endpoint = params.toString()
        ? `${API_ENDPOINTS.STATE_MACHINE.WORKFLOW_ANALYTICS(artifactId)}?${params.toString()}`
        : API_ENDPOINTS.STATE_MACHINE.WORKFLOW_ANALYTICS(artifactId);

      return await this.get<any>(endpoint);
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching workflow analytics:', error);
      return null;
    }
  }

  /**
   * Get state distribution for an artifact
   */
  static async getStateDistribution(artifactId: string): Promise<StateDistribution[]> {
    try {
      const response = await this.get<StateDistribution[] | { distribution: StateDistribution[] }>(
        API_ENDPOINTS.STATE_MACHINE.STATE_DISTRIBUTION(artifactId)
      );
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'distribution' in response) {
        return (response as { distribution: StateDistribution[] }).distribution;
      }
      return [];
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching state distribution:', error);
      return [];
    }
  }

  /**
   * Get artifact state summary
   */
  static async getArtifactStateSummary(artifactId: string): Promise<ArtifactStateSummary | null> {
    try {
      return await this.get<ArtifactStateSummary>(API_ENDPOINTS.STATE_MACHINE.ARTIFACT_STATE_SUMMARY(artifactId));
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching artifact state summary:', error);
      return null;
    }
  }

  /**
   * Get artifact item summary
   */
  static async getArtifactItemSummary(artifactId: string): Promise<ArtifactItemSummary | null> {
    try {
      return await this.get<ArtifactItemSummary>(API_ENDPOINTS.STATE_MACHINE.ARTIFACT_ITEM_SUMMARY(artifactId));
    } catch (error) {
      console.error('[StateMachineReportingService] Error fetching artifact item summary:', error);
      return null;
    }
  }
}

